import { useCallback, useEffect, useRef, useState } from 'react'
import { useParams } from 'react-router-dom'
import { fetchKioskTrack, type KioskTrack, type TrackState } from '../services/kioskTrack'
import { formatTRY } from '../utils/money'
import { BellIcon, BellRingIcon, ChefHatIcon, CheckIcon, ClockIcon, HeartIcon, WalletIcon, XIcon } from '../components/icons'
import './KioskTrackPage.css'

/** Sayfa açıkken durum bu aralıkla sorulur (yereldeki demo daha sık). */
const pollMs = (token: string) => (import.meta.env.DEV && token === 'demo' ? 1_000 : 5_000)

const STEPS: { state: TrackState; label: string }[] = [
  { state: 'odeme', label: 'Ödeme' },
  { state: 'sirada', label: 'Sırada' },
  { state: 'hazirlaniyor', label: 'Hazırlanıyor' },
  { state: 'hazir', label: 'Hazır' },
]

const TEXT: Record<TrackState, { title: string; sub: string }> = {
  odeme: { title: 'Ödeme bekleniyor', sub: 'Kasada ödemenizi yapın, siparişiniz hemen hazırlanmaya başlar.' },
  sirada: { title: 'Siparişiniz sırada', sub: 'Ödemeniz alındı, mutfağa iletildi.' },
  hazirlaniyor: { title: 'Hazırlanıyor', sub: 'Siparişiniz şu an hazırlanıyor.' },
  hazir: { title: 'Siparişiniz hazır!', sub: 'Tezgaha gelip kodunuzu söyleyerek alabilirsiniz.' },
  teslim: { title: 'Teslim edildi', sub: 'Afiyet olsun! Bizi tercih ettiğiniz için teşekkürler.' },
  iptal: { title: 'Sipariş iptal edildi', sub: 'Bir sorun olduysa kasaya sorabilirsiniz.' },
}

const ICON: Record<TrackState, (p: object) => JSX.Element> = {
  odeme: WalletIcon,
  sirada: ClockIcon,
  hazirlaniyor: ChefHatIcon,
  hazir: CheckIcon,
  teslim: HeartIcon,
  iptal: XIcon,
}

/** Kısa iki notalı zil; iOS'ta ses ancak bir dokunuştan sonra çalabildiği için bağlam önceden açılıyor. */
function chime(ctx: AudioContext) {
  const t = ctx.currentTime
  for (const [i, f] of [880, 1320].entries()) {
    const o = ctx.createOscillator()
    const g = ctx.createGain()
    o.frequency.value = f
    g.gain.setValueAtTime(0.0001, t + i * 0.22)
    g.gain.exponentialRampToValueAtTime(0.35, t + i * 0.22 + 0.02)
    g.gain.exponentialRampToValueAtTime(0.0001, t + i * 0.22 + 0.5)
    o.connect(g).connect(ctx.destination)
    o.start(t + i * 0.22)
    o.stop(t + i * 0.22 + 0.55)
  }
}

/**
 * Kiosk siparişinin takip sayfası (31 Eki 2026): kiosk ekranındaki karekoddan
 * gelir, girişsiz. Tarayıcı sekmesi açık kaldıkça durum güncellenir; telefon
 * uyuttuğu sekmeye dönülünce hemen yeniden sorulur.
 */
export function KioskTrackPage() {
  const { token = '' } = useParams()
  const [data, setData] = useState<KioskTrack | 'expired' | null | undefined>(undefined)
  const [sound, setSound] = useState(false)
  const audio = useRef<AudioContext | null>(null)
  const last = useRef<TrackState | null>(null)

  const load = useCallback(() => {
    fetchKioskTrack(token)
      .then((d) => setData(d))
      .catch(() => setData((prev) => (prev === undefined ? null : prev)))
  }, [token])

  const state = data && data !== 'expired' ? data.state : null
  // Hazır olduktan sonra da sorulmaya devam: teslim edilince sayfa güncellensin.
  const settled = state === 'teslim' || state === 'iptal'

  useEffect(() => {
    load()
    if (settled) return
    const t = window.setInterval(() => {
      if (document.visibilityState === 'visible') load()
    }, pollMs(token))
    const onShow = () => document.visibilityState === 'visible' && load()
    document.addEventListener('visibilitychange', onShow)
    return () => {
      window.clearInterval(t)
      document.removeEventListener('visibilitychange', onShow)
    }
  }, [load, settled, token])

  // Hazır olduğu an: ses (açıldıysa) ve titreşim (destekleyen telefonda).
  useEffect(() => {
    if (!state) return
    if (state === 'hazir' && last.current && last.current !== 'hazir') {
      if (audio.current) chime(audio.current)
      navigator.vibrate?.([200, 100, 200])
    }
    last.current = state
  }, [state])

  const toggleSound = () => {
    if (sound) {
      setSound(false)
      void audio.current?.close()
      audio.current = null
      return
    }
    const Ctx = window.AudioContext ?? (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext
    if (!Ctx) return
    audio.current = new Ctx()
    void audio.current.resume()
    setSound(true)
  }

  if (data === undefined) return <div className="ktrack ktrack--wait" />
  if (data === null || data === 'expired') {
    return (
      <div className="ktrack">
        <div className="ktrack__box">
          <h1>{data === 'expired' ? 'Bu sipariş artık geçerli değil' : 'Sipariş bulunamadı'}</h1>
          <p className="ktrack__muted">
            {data === 'expired'
              ? 'Takip sayfası yalnız sipariş günü açık kalır.'
              : 'Bağlantı hatalı olabilir. Kasaya sorabilirsiniz.'}
          </p>
        </div>
        <p className="ktrack__brand">Adissoo</p>
      </div>
    )
  }

  const t = TEXT[data.state]
  const Icon = ICON[data.state]
  // Teslim edildiyse bütün adımlar bitmiş.
  const at = data.state === 'teslim' ? STEPS.length : STEPS.findIndex((s) => s.state === data.state)

  return (
    <div className={`ktrack is-${data.state}`}>
      <div className="ktrack__box">
        <p className="ktrack__biz">{data.business}</p>

        <div className="ktrack__no">
          <span>Sıra numaranız</span>
          <b>{data.no ?? '-'}</b>
        </div>

        <div className="ktrack__status" role="status" aria-live="polite">
          <span className="ktrack__icon" aria-hidden="true">
            <Icon />
          </span>
          <div>
            <b>{t.title}</b>
            <span>{t.sub}</span>
          </div>
        </div>

        {data.state !== 'iptal' && (
          <ol className="ktrack__steps" aria-label="Sipariş adımları">
            {STEPS.map((s, i) => (
              <li key={s.state} className={i < at ? 'is-done' : i === at ? 'is-on' : ''}>
                <span className="ktrack__dot" aria-hidden="true">
                  {i < at || (i === at && s.state === 'hazir') ? <CheckIcon /> : null}
                </span>
                <span>{s.label}</span>
              </li>
            ))}
          </ol>
        )}

        {data.code && (
          <div className="ktrack__code">
            <span>Kasada ve teslimde söyleyeceğiniz kod</span>
            <b>{data.code}</b>
          </div>
        )}

        <div className="ktrack__items">
          <span className="ktrack__label">Siparişiniz</span>
          <ul>
            {data.items.map((i, n) => (
              <li key={n}>
                <span>{i.name}</span>
                <b>× {i.qty}</b>
              </li>
            ))}
          </ul>
          <div className="ktrack__total">
            <span>Toplam</span>
            <b>{formatTRY(data.totalKurus)}</b>
          </div>
        </div>

        {!settled && data.state !== 'hazir' && (
          <button type="button" className={`ktrack__sound ${sound ? 'is-on' : ''}`} onClick={toggleSound}>
            {sound ? <BellRingIcon /> : <BellIcon />}
            <span>{sound ? 'Hazır olunca ses çalacak' : 'Hazır olunca sesle haber ver'}</span>
          </button>
        )}
        {!settled && <p className="ktrack__muted ktrack__tip">Bu sayfa açık kaldıkça kendiliğinden güncellenir.</p>}
      </div>
      <p className="ktrack__brand">Adissoo</p>
    </div>
  )
}
